import { List, Typography, Avatar, Badge, Spin, Empty } from 'antd'
import { UserOutlined, TeamOutlined, FileTextOutlined } from '@ant-design/icons'
import { formatDistanceToNow } from 'date-fns'
import type { MessageWithAttachments } from '../../hooks/useRealtimeMessages'

const { Text, Paragraph } = Typography

interface MessageListProps {
  messages: MessageWithAttachments[]
  loading?: boolean
  onMessageClick?: (message: MessageWithAttachments) => void
  currentUserId?: string
}

export default function MessageList({ 
  messages, 
  loading = false,
  onMessageClick,
  currentUserId: _currentUserId
}: MessageListProps) {
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    )
  }

  if (!messages.length) {
    return (
      <Empty
        description="No messages yet"
        className="py-8"
      />
    )
  }

  return (
    <List
      className="message-list"
      itemLayout="horizontal"
      dataSource={messages}
      renderItem={(message) => {
        const isAdminMessage = message.is_admin_message
        const isUnread = !message.read_at && !isAdminMessage
        const hasAttachments = message.attachments && message.attachments.length > 0

        return (
          <List.Item
            className={`message-item ${isUnread ? 'bg-blue-50 dark:bg-blue-900/20' : ''} 
              hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors`}
            onClick={() => onMessageClick?.(message)}
          >
            <List.Item.Meta
              avatar={
                <Avatar 
                  icon={isAdminMessage ? <TeamOutlined /> : <UserOutlined />}
                  style={{ 
                    backgroundColor: isAdminMessage ? '#1890ff' : '#52c41a'
                  }}
                />
              }
              title={
                <div className="flex items-center gap-2">
                  <Text strong>
                    {message.sender_name || (isAdminMessage ? 'Admin' : 'Reporter')}
                  </Text>
                  {message.sender_role && (
                    <Badge 
                      count={message.sender_role} 
                      style={{ 
                        backgroundColor: isAdminMessage ? '#1890ff' : '#52c41a',
                        fontSize: '10px',
                        height: '16px',
                        lineHeight: '16px',
                        padding: '0 4px'
                      }} 
                    />
                  )}
                  {hasAttachments && (
                    <FileTextOutlined className="text-gray-400" />
                  )}
                  <Text type="secondary" className="text-xs ml-auto">
                    {message.created_at ? formatDistanceToNow(new Date(message.created_at), { addSuffix: true }) : 'N/A'}
                  </Text>
                </div>
              }
              description={
                <div>
                  <Paragraph 
                    ellipsis={{ rows: 2 }} 
                    className="mb-0 text-gray-700 dark:text-gray-300"
                  >
                    {message.content}
                  </Paragraph>
                  {hasAttachments && (
                    <Text type="secondary" className="text-xs">
                      {message.attachments.length} attachment{message.attachments.length > 1 ? 's' : ''}
                    </Text>
                  )}
                </div>
              }
            />
            {isUnread && (
              <Badge 
                status="processing" 
                text="New" 
                className="ml-4"
              />
            )}
          </List.Item>
        )
      }}
    />
  )
}